import { GridItem } from '@/constants/dashboard.layout'
import {
  basicCustomerPayload,
  isAllSelectedType,
  payloadDefaultProp,
} from '@/containers/header/dynamicDropdown'
import { FormDataTypes } from '@/formBuilder/formBuilder.types'
import {
  Condition,
  createViewPayload,
  MenusType,
  TrafficDataObj,
} from '@/services/types/api.types'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import L from 'leaflet'
import OnlineSites from '@/assets/SpectrotelIcons/OnlineSites.svg'
import HighCritical from '@/assets/SpectrotelIcons/HighCritical.svg'
import LowCritical from '@/assets/SpectrotelIcons/LowCritical.svg'
import { Filter } from '@/components/atom/newTable/tableFilters'

// ───────────────────────────────────────────────
// 🧠 Helper functions
// ───────────────────────────────────────────────

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const getStorageKey = (sub?: string, prefix?: string) => {
  const key = sub ? `${prefix}_${sub}` : null
  return key
}

export const saveToStorage = (
  sub: string | undefined,
  data: unknown,
  prefix?: string,
) => {
  const key = getStorageKey(sub, prefix)
  if (!key) return
  localStorage.setItem(key, JSON.stringify(data))
}

export const getFromStorage = (sub?: string, prefix?: string) => {
  const key = getStorageKey(sub, prefix)
  if (!key) return
  const stored = localStorage.getItem(key)
  if (!stored) return null
  try {
    return JSON.parse(stored)
  } catch (e) {
    console.error(`Error parsing localStorage key "${key}":`, e)
    return null
  }
}
export const removeFromStorage = (sub: string | undefined, prefix?: string) => {
  const key = getStorageKey(sub, prefix)
  if (!key) return
  localStorage.removeItem(key)
}
export const isRoot2Array = (value: unknown): value is Response[] => {
  return (
    Array.isArray(value) &&
    value.every(item => 'label' in item && 'picklistValues' in item)
  )
}

export const toTitleCase = (str: string) =>
  str.replace(
    /\w\S*/g,
    txt => txt[0].toUpperCase() + txt.substring(1).toLowerCase(),
  )

export const getMarkerIcon = (location: string | undefined = 'unknown') => {
  const status = location?.toLowerCase() || 'unknown'
  const isCriticalStatus =
    status === 'critical' ||
    status === 'degraded' ||
    status === 'offline' ||
    status === 'down'
  const isWarningStatus = status === 'warning'

  const iconUrl = isCriticalStatus
    ? HighCritical
    : isWarningStatus
      ? LowCritical
      : OnlineSites

  if (isCriticalStatus) {
    return L.divIcon({
      html: `
        <div style="position:relative;width:42px;height:42px;display:flex;align-items:center;justify-content:center;">
          <div style="position:absolute;width:42px;height:42px;border:4px solid #ef4444;border-radius:50%;animation:marker-pulse-animation 3s linear infinite;"></div>
          <div style="width:42px;height:42px;border-radius:50%;border:2px solid var(--danger);background-color:var(--danger);display:flex;align-items:center;justify-content:center;position:relative;z-index:2;box-shadow:0 4px 6px rgba(0,0,0,0.1);overflow:hidden;">
            <img src="${iconUrl}" style="width:100%;height:100%;object-fit:contain;" alt="critical" />
        </div>
        </div>
        <style>@keyframes marker-pulse-animation{0%{transform:scale(1);opacity:1}100%{transform:scale(1.5);opacity:0}}</style>
      `,
      className: '',
      iconSize: [42, 42],
      iconAnchor: [21, 42],
    })
  }

  if (isWarningStatus) {
    return L.divIcon({
      html: `
        <div style="position:relative;width:42px;height:42px;display:flex;align-items:center;justify-content:center;">
          <div style="position:absolute;width:42px;height:42px;border:4px solid var(--warning-foreground,#fbbf24);border-radius:50%;animation:marker-pulse-animation 3s linear infinite;"></div>
          <div style="width:42px;height:42px;border-radius:50%;border:2px solid var(--warning);background-color:var(--warning-dim-light);display:flex;align-items:center;justify-content:center;position:relative;z-index:2;box-shadow:0 4px 6px rgba(0,0,0,0.1);overflow:hidden;">
            <img src="${iconUrl}" style="width:100%;height:100%;object-fit:contain;" alt="warning" />
        </div>
        </div>
        <style>@keyframes marker-pulse-animation{0%{transform:scale(1);opacity:1}100%{transform:scale(1.5);opacity:0}}</style>
      `,
      className: '',
      iconSize: [42, 42],
      iconAnchor: [21, 42],
    })
  }

  // Online / default
  return L.divIcon({
    html: `
      <div style="width:42px;height:42px;border-radius:50%;border:2px solid var(--success);background-color:var(--success-dim-light);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 6px rgba(0,0,0,0.1);overflow:hidden;">
        <img src="${iconUrl}" style="width:100%;height:100%;object-fit:contain;" alt="online" />
      </div>
    `,
    className: '',
    iconSize: [42, 42],
    iconAnchor: [21, 42],
  })
}

export function bytesToMegabits(bytes: number): number {
  return (bytes * 8) / 1_000_000
}

export type GroupedFormData = {
  [key: string]: FormDataTypes[]
}

export type SortConfigType = { header: string; direction: boolean }

export const ArrayofObjectGroupBy = (
  arrayData: FormDataTypes[],
): GroupedFormData => {
  return arrayData.reduce<GroupedFormData>((group, item) => {
    const accName = item.accName!

    group[accName] = group[accName] || []
    group[accName].push(item)

    return group
  }, {})
}

export function convertValuesToMegabits(dataArray: TrafficDataObj[]) {
  return dataArray.map(item => {
    const newItem: TrafficDataObj = { ...item }
    Object.keys(newItem).forEach(key => {
      if (key !== 'time' && typeof newItem[key] === 'number') {
        ;(newItem[key] as number) = bytesToMegabits(newItem[key])
      }
    })
    return newItem
  })
}

export const transformNodeId = (nodeId: string) => {
  return nodeId.replace(/NODEID_(\d+)/, 'NODE $1 :')
}

export function convertSecondsToHours(seconds: string): string {
  const hours = parseFloat(seconds) / 3600
  return hours % 1 === 0 ? hours.toString() : hours.toFixed(2)
}

export function convertSecondsToHHMMSS(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

export const getConditions = (data: Condition[]) => {
  const filteredData = data.filter(
    (item: Condition) => Array.isArray(item.value) && item.value.length > 0,
  )
  const conditions = filteredData.map((item: Condition, index: number) => {
    const conditionObj = {
      condition: {
        field: item.field,
        operator: 'IN',
        value: item.value,
      },
    }
    if (index < filteredData.length - 1) {
      return { ...conditionObj, separator: 'AND' }
    }
    return conditionObj
  })
  return conditions
}

export function formatCamelCase(value: string): string {
  return value
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString)

  const options: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  }
  return date.toLocaleString('en-US', options)
}
export const formattedDate = (dateStr: string | Date) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })

export function timeAgo(isoTime: string | undefined): string {
  if (!isoTime) return 'Unknown'

  const inputDate = new Date(isoTime)
  const now = new Date()
  const diffInMs = now.getTime() - inputDate.getTime()
  const diffInSeconds = Math.floor(diffInMs / 1000)

  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  }

  for (const [key, value] of Object.entries(intervals)) {
    const count = Math.floor(diffInSeconds / value)
    if (count > 0) {
      return count === 1 ? `1 ${key} ago` : `${count} ${key}s ago`
    }
  }

  return 'just now'
}

export const convertToUserTimezone = (
  timestamp: string | number | Date,
  locale: string = 'en-US',
): string => {
  const timezoneMap: { [key: string]: string } = {
    'GMT+5:30': 'IST',
    'GMT-5:00': 'EST',
    'GMT-8:00': 'PST',
  }

  try {
    const date = new Date(timestamp)
    if (isNaN(date.getTime())) {
      throw new Error('Invalid timestamp')
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    })

    let formattedDate = formatter.format(date)

    const offsetMatch = formattedDate.match(/GMT[+-]\d{1,2}:\d{2}/)

    if (offsetMatch) {
      const offset = offsetMatch[0]
      const abbreviation = timezoneMap[offset] || offset
      formattedDate = formattedDate.replace(offset, abbreviation)
    }

    return formattedDate
  } catch (error) {
    console.error("Error converting to user's timezone:", error)
    return 'Invalid timestamp'
  }
}

export const getDateStatus = (timestamp: string): string => {
  if (!timestamp) return 'Unknown'
  const givenDate = new Date(timestamp)
  const currentDate = new Date()

  if (givenDate < currentDate) {
    return `${givenDate.toISOString().split('T')[0]}`
  } else {
    const timeDifference = givenDate.getTime() - currentDate.getTime()
    const daysLeft = Math.ceil(timeDifference / (1000 * 60 * 60 * 24))
    return `${daysLeft} Days remaining`
  }
}

export const getDateStatusWidget = (timestamp: string): string => {
  if (!timestamp) return 'Unknown'
  const givenDate = new Date(timestamp)
  const currentDate = new Date()

  if (givenDate < currentDate) {
    return `${givenDate.toISOString().split('T')[0]}`
  } else {
    const timeDifference = givenDate.getTime() - currentDate.getTime()
    const daysLeft = Math.ceil(timeDifference / (1000 * 60 * 60 * 24))
    return `${daysLeft}`
  }
}

export function getDateRange(time: string): {
  fromDate: string
  toDate: string
} {
  const today = new Date()
  let fromDate: Date
  let toDate: Date

  switch (time) {
    case 'LAST_WEEK': {
      const day = today.getDay() // 0 (Sun) to 6 (Sat)
      const diffToMonday = day === 0 ? 6 : day - 1

      toDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - diffToMonday - 1,
      ) // last Sunday
      fromDate = new Date(
        toDate.getFullYear(),
        toDate.getMonth(),
        toDate.getDate() - 6,
      ) // last Monday
      break
    }

    case 'LAST_MONTH': {
      const year = today.getFullYear()
      const month = today.getMonth() // 0-based

      fromDate = new Date(year, month - 1, 1)
      toDate = new Date(year, month, 0)
      toDate.setHours(23, 59, 59, 999) // Ensure full day is included
      break
    }

    case 'LAST_7_DAYS': {
      toDate = new Date(today) // today
      fromDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 6,
      ) // 6 days ago
      break
    }

    case 'LAST_30_DAYS': {
      toDate = new Date(today) // today
      fromDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 29,
      ) // 29 days ago
      break
    }

    default: {
      fromDate = today
      toDate = today
    }
  }

  return {
    fromDate: fromDate.toISOString().split('T')[0],
    toDate: toDate.toISOString().split('T')[0],
  }
}

export function getDaysRemaining(dateString: string): number {
  const today = new Date()
  const targetDate = new Date(dateString)

  // Reset time to 00:00:00 for accurate day difference
  today.setHours(0, 0, 0, 0)
  targetDate.setHours(0, 0, 0, 0)

  const diffInMs = targetDate.getTime() - today.getTime()
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24))

  return diffInDays
}

export function calculateDaysFromDate(date: Date): number {
  const currentDate = new Date()
  const timeDifference = date.getTime() - currentDate.getTime()
  return Math.floor(timeDifference / (1000 * 60 * 60 * 24))
}

export function formatDateTime(
  dateTimeString: string | null | undefined,
): string | null {
  if (!dateTimeString) return null

  const date = new Date(dateTimeString)
  if (isNaN(date.getTime())) return null

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }

  return date.toLocaleString('en-US', options)
}

export function formatDateWithoutTime(
  dateTimeString: string | null | undefined,
): string | null {
  if (!dateTimeString) return null

  const date = new Date(dateTimeString)
  if (isNaN(date.getTime())) return null

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }

  return date.toLocaleString('en-US', options)
}

type ISO8601DateTime =
  `${number}-${number}-${number}T${number}:${number}:${number}.${number}${'+' | '-'}${number}`

export function isISO8601DateTime(value: string): value is ISO8601DateTime {
  const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}[+-]\d{4}$/
  return regex.test(value)
}

export function capitalizeFirstLetter(str: string | null) {
  if (!str) return ''
  return str
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

export function formatDateByLocale(inputString: string): string {
  const date = new Date(inputString)

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date string')
  }

  const userLocale: string = navigator.language || 'en-US'

  const formattedDate: string = new Intl.DateTimeFormat(userLocale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date)

  return formattedDate
}

// Helper function to convert ISO date strings to HTML input format
export const convertDateForInput = (
  dateString: string,
  inputType: 'date' | 'datetime-local',
): string => {
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return ''
    }

    if (inputType === 'date') {
      // Format as YYYY-MM-DD for date inputs
      return date.toISOString().split('T')[0]
    } else {
      // Format as YYYY-MM-DDTHH:MM for datetime-local inputs
      const isoString = date.toISOString()
      return isoString.slice(0, 16) // Remove seconds and timezone
    }
  } catch (error) {
    console.warn(`Failed to convert date ${dateString}:`, error)
    return ''
  }
}

export function debounce<Args extends unknown[]>(
  func: (...args: Args) => void,
  delay: number,
): (...args: Args) => void {
  let timer: NodeJS.Timeout

  return function (...args: Args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func(...args)
    }, delay)
  }
}

export function toCamelCase(str: string) {
  return str
    .split(/[-_]/)
    .map((word, index) => {
      if (index === 0) {
        return word
      }
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join('')
}

export const formatDefPayload = (
  payload: payloadDefaultProp,
): basicCustomerPayload &
  isAllSelectedType & { billingSubGroup?: string[] } => {
  const isAllSelected = payload.isAllSelected || false
  if (payload.billingSubGroup) {
    // localStorage.setItem('billingSubGroup', payload.billingSubGroup)
    if (Array.isArray(payload.siteId) && payload.siteId.length === 0) {
      return { customerId: payload.customerId, siteId: [], isAllSelected }
    }
    return {
      customerId: [],
      billingSubGroup: [payload.billingSubGroup],
      siteId: payload.siteId,
      isAllSelected,
    }
  } else if (Array.isArray(payload.siteId) && payload.siteId.length === 0) {
    return { customerId: payload.customerId, siteId: [], isAllSelected }
  } else {
    return {
      customerId: payload.customerId,
      siteId: payload.siteId,
      isAllSelected,
    }
  }
}

export const formatText = (text: string): string => {
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function formatDateToYYYYMMDD(dateString: string) {
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`.toString()
}

export const formatDateInReport = (dateString: string) => {
  const [year, month, day] = dateString.split('-')
  const formattedMonth = month.padStart(2, '0')
  const formattedDay = day.padStart(2, '0')

  return `${formattedDay}/${formattedMonth}/${year}`
}

export const getCurrentMonthRange = (
  date?: string,
): { start: string; end: string } => {
  const now = date ? new Date(date) : new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1

  const start = `${year}-${month.toString().padStart(2, '0')}-01`
  const lastDay = new Date(year, month, 0).getDate()
  const end = `${year}-${month.toString().padStart(2, '0')}-${lastDay}`

  return { start, end }
}

export function formatNumber(number: number) {
  return number.toLocaleString()
}

export function isValidDate(value: string): boolean {
  return typeof value === 'string' && !isNaN(new Date(value).getTime())
}
export const formatServiceCost = (cost: number | string | null): string => {
  if (typeof cost === 'string') {
    return cost
  }
  if (cost == null) {
    return ''
  }
  if (cost < 1 && cost != 0) {
    return `$${cost.toFixed(2)}`
  } else if (cost == 0) {
    return `$${cost.toFixed(2)}`
  }
  return `$${cost.toFixed(2)}`
}

export const formatBillCost = (cost: number | null): string => {
  if (typeof cost === 'string') {
    return cost
  }
  if (cost == null) {
    return ''
  }

  const formattedCost = Number(cost).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  if (cost < 0) {
    return `($${formattedCost.replace('-', '')})`
  }

  return `$${formattedCost}`
}

export const formatCurrencyWithK = (
  value: number | string | null | undefined,
): string => {
  if (value == null) return ''
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return ''

  const absNum = Math.abs(num)
  let formatted = ''

  if (absNum >= 1_000_000_000) {
    formatted = `${parseFloat((absNum / 1_000_000_000).toFixed(2))}B`
  } else if (absNum >= 1_000_000) {
    formatted = `${parseFloat((absNum / 1_000_000).toFixed(2))}M`
  } else if (absNum >= 1_000) {
    formatted = `${parseFloat((absNum / 1_000).toFixed(2))}k`
  } else {
    formatted = absNum.toFixed(2)
  }

  return num < 0 ? `($${formatted})` : `$${formatted}`
}

export type TimeParts = {
  days?: number
  hours?: number
  mins?: number
}

export const convertMinutesToTimeFormat = (minutes: number): TimeParts => {
  const days = Math.floor(minutes / (60 * 24))
  const hours = Math.floor((minutes % (60 * 24)) / 60)
  const mins = minutes % 60

  const parts: TimeParts = {}
  if (days > 0) parts.days = days
  if (hours > 0) parts.hours = hours
  if (mins > 0) parts.mins = mins

  return parts
}

export const recalculateLayout = (
  updatedItems: GridItem[],
  numOfCols: number,
): GridItem[] => {
  let x = 0
  let y = 0

  return updatedItems.map(item => {
    if (x + item?.w > numOfCols) {
      x = 0
      y += 2
    }
    const newItem = { ...item, x, y }
    x += item?.w
    return newItem
  })
}

// export const isDateTime = (value: unknown): boolean => {
//   if (value instanceof Date) {
//     return !isNaN(value.getTime())
//   }

//   if (typeof value === 'string') {
//     const format1 = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3}$/
//     const format2 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}[+-]\d{4}$/

//     if (!format1.test(value) && !format2.test(value)) {
//       return false
//     }

//     const parsedDate = new Date(value)
//     return !isNaN(parsedDate.getTime())
//   }

//   return false
// }

export const isDateTime = (value: unknown): boolean => {
  if (value instanceof Date) {
    return !isNaN(value.getTime())
  }

  if (typeof value === 'string') {
    // Format for datetime with time (e.g., YYYY-MM-DD HH:mm:ss.sss)
    const format1 = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3}$/
    // Format for ISO 8601 datetime with time and timezone (e.g., 2021-09-08T16:45:00.000+02:00)
    const format2 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}[+-]\d{4}$/
    // Format for date without time (e.g., YYYY-MM-DD)
    const format3 = /^\d{4}-\d{2}-\d{2}$/

    if (!(format1.test(value) || format2.test(value) || format3.test(value))) {
      return false
    }

    // Attempt to parse the date
    const parsedDate = new Date(value)
    return !isNaN(parsedDate.getTime())
  }

  return false
}

export const isBoolean = (value: unknown): boolean => {
  return typeof value === 'boolean'
}

export function boolToStringYesNo(value: unknown): string {
  return value ? 'Yes' : 'No'
}

export function isStatus(value: string) {
  return value === 'ACTIVE' || value === 'DISCONNECTED' || value === 'PENDING'
}

export function getCellClassName(
  columnLabel: string,
  columnValue: string,
  colIndex: number,
): string {
  const baseClass =
    'max-w-[750px] truncate leading-[1.25rem] w-fit min-h-[26px]'
  const paddingClass = colIndex === 0 ? 'pl-2' : ''

  if (columnLabel === 'Status') {
    if (['ACTIVE', 'Active', 'Healthy'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-success-dim-light text-success-forground py-1 px-2 rounded-full font-semibold`
    }
    if (['DISCONNECTED', 'Degraded'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-danger-dim-light text-danger-foreground py-1 px-2 rounded-full font-semibold`
    }
    if (['PENDING', 'Pending'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-warning-dim-light text-warning-foreground py-1 px-2 rounded-full font-semibold`
    }
    if (
      [
        'Awaiting Carrier Response',
        'Awaiting Customer Response',
        'Billing Review',
        'Canceled',
        'Check Carrier Response',
        'Cleared Awaiting Verification',
        'Customer Follow Up Needed',
        'Deferred',
        'Fiber Conversion',
        'Fix Action Rejected',
        'Inside Wire Dispatch',
        'In Work',
        'Maintenance Event',
        'Network Event',
        'New',
        'Change-Listing',
        'Open',
        'Out To Carrier',
        'Pending',
        'Pending Order Completion',
        'Tech Follow Up Needed',
        'Vendor Meet',
        'Closed Merged',
        'Carrier Dispatch In',
        'Carrier No Access',
        'Carrier Testing',
        'Carrier Dispatch Pending',
        'Carrier Technician Pre-Assigned',
        'Carrier Network Outage',
        'Carrier Dispatch Out',
        'Cable Failure',
        'Carrier Screening',
        'Dispatch Pending',
        'In Progress',
        'Referred to Provider/Vendor',
        'Field Dispatch',
        'Follow Up',
        'Referred Internally',
        'On Hold',
        'Resolved',
        'Closed',
        'Escalated',
        'Critical',
        'Resolved - Pending Close',
        'Queued',
        'Pending Close',
        'Hotlist',
        'Equipment Vendor Dispatch',
        'Deferred Pending Load',
        'TCB',
        'INWORK',
        'PROVISIONING',
        'PENDING_CLOSE',
        'Respond to Carrier',
        'Sent To Carrier',
        'Select',
        'Carrier Dispatch',
        'Technician Pre-Assigned',
        'Check Provider/Vendor Response',
      ].includes(columnValue)
    ) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-info-dim-light border-info-dim-light text-info-forground py-1 px-2 rounded-full font-semibold`
    }
    if (['Transfer'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-transfer-dim-light-light-light border-transfer-dim-light-light-light text-transfer-foreground py-1 px-2 rounded-full font-semibold`
    }
  }

  if (columnLabel === 'Order Status') {
    if (['PAST_DUE', 'Past Due'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-danger-dim-light text-danger-foreground py-1 px-2 rounded-full font-semibold`
    }
    if (['IN_PROGRESS', 'In Progress'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-warning-dim-light text-warning-foreground py-1 px-2 rounded-full font-semibold`
    }
    if (['COMPLETED', 'Completed'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-success-dim-light text-success-forground py-1 px-2 rounded-full font-semibold`
    }
  }

  if (columnLabel === 'Node Status') {
    if (['ONLINE', 'Online'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-success-dim-light text-success-forground py-1 px-2 rounded-full font-semibold`
    }
    if (['DEGRADED', 'Degraded', 'Critical'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-danger-dim-light text-danger-foreground py-1 px-2 rounded-full font-semibold`
    }
    if (['WARNING', 'Warning'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-warning-dim-light text-warning-foreground py-1 px-2 rounded-full font-semibold`
    }
  }

  if (columnLabel === 'Type') {
    if (['BILL', 'Bill'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-success-dim-light text-success-forground py-1 px-2 rounded-full font-semibold`
    }
    if (['PAYMENT', 'Payment'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-info-dim-light text-info-forground py-1 px-2 rounded-full font-semibold`
    }
  }

  if (columnLabel === 'Site Status') {
    return `${baseClass} ${paddingClass} text-[0.8rem]`
  }

  if (columnLabel === 'Priority') {
    if (['Critical', 'Critical'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-danger-dim-light border-danger-dim-light text-danger-foreground py-1 px-2 rounded-full font-semibold`
    }
    if (['High', 'High'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-warning-dim-light border-warning-dim-light text-warning-foreground py-1 px-2 rounded-full font-semibold`
    }
    if (['Medium', 'Medium'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-info-dim-light border-info-dim-light text-info-forground py-1 px-2 rounded-full font-semibold`
    }
    if (['Low', 'Low'].includes(columnValue)) {
      return `${baseClass} ${paddingClass} text-[0.8rem] w-fit bg-hold-dim-light border-hold-dim-light text-hold-foreground py-1 px-2 rounded-full font-semibold`
    }
  }

  return `${baseClass} ${paddingClass} text-primary-foreground text-table-td `
}

export function formatNumberPositiveorNegative(num: number) {
  return num > 0 ? `+${num}` : `${num} `
}

export function isPositiveNumber(num: number) {
  return num > 0
}
export function isGreaterorEqualThanZero(num: number) {
  return num >= 0
}

export interface WidgetMap {
  [key: string]: { key: string; name: string; hasExpandedView: boolean }[]
}

export function extractWidgetsWithExpandedView(data: MenusType): WidgetMap {
  const result: WidgetMap = {}
  console.log(data, 'datdatatatat')

  function extract(item: MenuItem, label: string) {
    console.log(item, 'cdcdcdcdcdcdcc')

    if (item.widgets) {
      result[label] = Object.entries(item.widgets).map(([key, widget]) => ({
        key,
        name: widget.name || key + 1,
        hasExpandedView: widget.hasExpandedView,
      }))
    }

    if (item.children && item.children.length > 0) {
      item.children.forEach(child => {
        extract(child, item.name || child.name || child.id)
      })
    }
  }

  data.forEach(menu => {
    extract(menu, menu.name)
  })

  return result
}

export function formatCamelCaseLabel(input: string): string {
  const withSpaces = input.replace(/_/g, ' ')
  const spaced = withSpaces.replace(/([a-z])([A-Z])/g, '$1 $2')
  return spaced
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

type Widget = {
  name: string
  permission: string[]
  hasExpandedView: boolean
}

export type MenuItem = {
  id: string
  name?: string
  label?: string
  path?: string
  icon?: string
  permissions?: string[]
  children?: MenuItem[]
  widgets?: Record<string, Widget>
}

export const extractAllowedPaths = (menus: MenuItem[]): string[] => {
  const paths: Set<string> = new Set()

  const recurse = (items: MenuItem[]): void => {
    for (const item of items) {
      if (item.path) paths.add(item.path)
      if (item.children?.length) recurse(item.children)
    }
  }

  recurse(menus)
  return Array.from(paths)
}

export function extractPagePermissions(menuItems: MenusType) {
  const result: { [key: string]: string[] } = {}

  menuItems.forEach(menu => {
    if (Array.isArray(menu.children)) {
      menu.children.forEach(page => {
        if (page.id && Array.isArray(page.permissions)) {
          result[page.id] = page.permissions
        }
      })
    }
  })

  return result
}

export function isFilterActive(str: { field: string; value: string[] }[]) {
  return str.some(item => Array.isArray(item.value) && item.value.length > 0)
}

type Item = {
  key: string
  name: string
  hasExpandedView: boolean
}

type ValueItem = {
  key?: string
  name: string
  value: boolean
}

export function addValues(sourceArr: ValueItem[], valueMap: Item[]) {
  const sourceMap = new Map(sourceArr.map(item => [item.key, item.value]))

  return valueMap.map(val => ({
    key: val.key,
    name: val.name,
    value: sourceMap.has(val.key)
      ? sourceMap.get(val.key)!
      : !['packetloss_metrics', 'latency_metrics', 'order_metrics'].includes(
          val.key,
        ),
  }))
}

export function formatNumbers(val: number): string {
  if (val > 0 && val < 10) {
    return `0${val}`
  }
  if (val >= 99999) {
    return `${(val / 1000).toFixed(1)}K`
  }
  return val.toString()
}

export function hasNegativeSign(str: string) {
  return str.includes('-')
}

export function formatDollar(num: number) {
  const absValue = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  if (num > 0) {
    return `+$${absValue}`
  } else if (num < 0) {
    return `-$${absValue}`
  } else {
    return `$${absValue}`
  }
}

export const transformFilters = (filters: Record<string, Filter>) => {
  const transformed: Record<string, unknown> = {}

  Object.entries(filters).forEach(([key, filter]) => {
    const { value } = filter

    switch (key) {
      case 'IsClosed':
        if (Array.isArray(value) && value.length === 1) {
          transformed[key] = value[0] === 'true'
        }
        break

      case 'type':
        if (Array.isArray(value)) {
          value.forEach(item => {
            if (item === 'incidents') {
              Object.assign(transformed, {
                search: 'TT -',
                searchFields: ['Ticket_Type__c'],
              })
            } else if (item === 'changes') {
              Object.assign(transformed, {
                Ticket_Type__c: ['Eng - Change Request'],
              })
            } else if (item === 'other') {
              Object.assign(transformed, { showOtherCases: true })
            }
          })
        }
        break

      default:
        transformed[key] = value
        break
    }
  })

  return transformed
}

function base64UrlDecode(str: string) {
  str = str.replace(/-/g, '+').replace(/_/g, '/')
  while (str.length % 4) str += '='
  return decodeURIComponent(
    atob(str)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  )
}

export function decodeJwt(token: string) {
  const [h, p] = token.split('.')
  if (!h || !p) throw new Error('Not a valid JWT')
  const header = JSON.parse(base64UrlDecode(h))
  const payload = JSON.parse(base64UrlDecode(p))
  return { header, payload }
}

export function formatPercentage(value: number | string): string {
  const num =
    typeof value === 'string' ? parseFloat(value.replace('%', '')) : value

  if (isNaN(num)) return '0%'

  return `${num.toFixed(2)}%`
}

export function formatNumberWithIntendation(val: number): string {
  if (val > 0 && val < 10) return `0${val}`

  const units = [
    { value: 1_000_000_000_000, symbol: 'T' }, // Trillion
    { value: 1_000_000_000, symbol: 'B' }, // Billion
    { value: 1_000_000, symbol: 'M' }, // Million
    { value: 1_000, symbol: 'k' }, // Thousand
  ]

  for (const unit of units) {
    if (val >= unit.value) {
      return `${parseFloat((val / unit.value).toFixed(2))}${unit.symbol}`
    }
  }

  return val.toString()
}

// Helper function to get color based on percentage value (lower is better)
export const getColorByPercentage = (value: number): string => {
  if (value <= 50) {
    return 'text-success'
  } else if (value <= 80) {
    return 'text-warning'
  } else {
    return 'text-danger'
  }
}

// Helper function for response time (lower is better, but not 0)
export const getColorForResponseTime = (
  value: number,
  threshold: number,
): string => {
  if (value === 0) {
    return 'text-danger'
  }
  const percentage = (value / threshold) * 100
  if (percentage <= 50) {
    return 'text-success'
  } else if (percentage <= 80) {
    return 'text-warning'
  } else {
    return 'text-danger'
  }
}

// Helper function for uptime (higher is better)
export const getColorForUptime = (value: number): string => {
  if (value >= 99) {
    return 'text-success'
  } else if (value >= 95) {
    return 'text-warning'
  } else {
    return 'text-danger'
  }
}

// Helper function for packet loss (lower is better, 0 is best)
export const getColorForPacketLoss = (value: number): string => {
  if (value <= 1) {
    return 'text-success'
  } else if (value <= 5) {
    return 'text-warning'
  } else {
    return 'text-danger'
  }
}

export const formatNumberCompact = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0'
  }

  const absValue = Math.abs(value)

  if (absValue >= 1_000_000_000) {
    return `${parseFloat((value / 1_000_000_000).toFixed(2))}B`
  }

  if (absValue >= 1_000_000) {
    return `${parseFloat((value / 1_000_000).toFixed(2))}M`
  }

  if (absValue >= 1_000) {
    return `${parseFloat((value / 1_000).toFixed(2))}k`
  }

  return value.toString()
}

export const buildFiltersFromView = (
  view: createViewPayload,
): Record<string, Filter> => {
  const result: Record<string, Filter> = {}

  view.filters?.forEach(f => {
    let value: Filter['value'] = f.values

    if (f.type === 'daterange' || f.type === 'datetimerange') {
      const vals = f.values as string[]
      value = { fromDate: vals[0], toDate: vals[1] }
    }

    result[f.name] = { id: f.name, type: f.type as Filter['type'], value }
  })

  return result
}
